import classNames from 'classnames'
import moment from 'moment'
import React from 'react'
import { Column, useTable, useSortBy, usePagination } from 'react-table'
import { IGetRidersPerPromotion, IPromotion, IRiderReponseObject } from 'src/types'
import { Link, Redirect } from 'react-router-dom'
import ReactTablePagination from '../ui/ReactTablePagination'
import BackToList from '../ui/BackToList'

interface IRidersPerPromotioProps {
  promotionId: string
  promotion: IPromotion
  riders: IRiderReponseObject[]
  riderCount: number
  getRidersPerPromotion: (params: IGetRidersPerPromotion) => void
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  pageCount: number
  fetchData: (params: { pageIndex: number; pageSize: number }) => void
}

const PAGE_SIZE = 10

function Table<T extends object>({
  columns,
  data,
  fetchData,
  loading,
  // filters,
  pageCount: controlledPageCount
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { pageIndex, pageSize, sortBy } = state

  React.useEffect(() => {
    fetchData({ pageIndex, pageSize })
  }, [pageIndex, pageSize])

  React.useEffect(() => {
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })
    fetchData({ pageIndex, pageSize })
  }, [sortBy])

  return (
    <React.Fragment>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  className={classNames('', {
                    sortable: column.canSort,
                    sortedAsc: column.isSortedDesc === false,
                    sortedDesc: column.isSortedDesc === true
                  })}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td>Loading...</td>
            </tr>
          ) : (
            rows.map(
              (row, i) =>
                +prepareRow(row) || (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, index) => {
                      return (
                        <td {...cell.getCellProps()} key={`1-${index}`}>
                          {cell.render('Cell')}
                        </td>
                      )
                    })}
                  </tr>
                )
            )
          )}
        </tbody>
      </table>
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

const renderEmail = (rider: IRiderReponseObject) => {
  if (!rider || !rider.email) {
    return ''
  }
  return rider.email.emailAddress
}
const renderName = (rider: IRiderReponseObject) => {
  if (!rider || !rider.firstName || !rider.lastName) {
    return ''
  }
  return `${rider.firstName} ${rider.lastName}`
}

const renderUsername = (rider: IRiderReponseObject) => {
  if (!rider || !rider.username) {
    return ''
  }
  return rider.username
}
const renderPhonenumber = (rider: IRiderReponseObject) => {
  if ((!rider || !rider.phones) && rider.phones.length > 0) {
    return ''
  }
  return `${rider.phones[0].countryCode}${rider.phones[0].phoneNumber}`
}

const renderCreatedDate = (rider: IRiderReponseObject) => {
  if (!rider || !rider.createdAt) {
    return ''
  }
  return moment(rider.createdAt).format('MMM DD YYYY')
}

export const RidersPerPromotion = (props: IRidersPerPromotioProps) => {
  if (!props.promotion) {
    return <Redirect to={'/promotions'} />
  }
  const [loading, setLoading] = React.useState(false)
  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        Header: 'Name',
        width: 200,
        accessor: (rider: IRiderReponseObject) => {
          return <Link to={`/riders/${rider.id}/profile/info`}>{renderName(rider)}</Link>
        }
      },
      {
        id: 'phone',
        Header: 'Phone Number',
        accessor: (rider: IRiderReponseObject) => renderPhonenumber(rider),
        disableSortBy: true,
        className: 'flex items-center'
      },
      {
        id: 'email',
        Header: 'Email',
        accessor: (rider: IRiderReponseObject) => renderEmail(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'username',
        Header: 'Username',
        accessor: (rider: IRiderReponseObject) => renderUsername(rider),
        className: 'RidersListItemEmail flex items-center'
      },
      {
        id: 'createdAt',
        Header: 'Created Date',
        accessor: (rider: IRiderReponseObject) => renderCreatedDate(rider),
        className: 'flex items-center'
      }
    ],
    []
  )
  React.useEffect(() => {
    props.getRidersPerPromotion({ promotionId: props.promotionId, active: props.promotion.active })
  }, [])
  const fetchData = React.useCallback(async ({ pageSize, pageIndex }) => {
    setLoading(true)
    props.getRidersPerPromotion({
      promotionId: props.promotionId,
      active: props.promotion.active,
      skip: pageSize * pageIndex,
      take: pageSize
    })
    setLoading(false)
  }, [])

  const pageCount = Math.ceil(props.riderCount / PAGE_SIZE)
  return (
    <React.Fragment>
      <BackToList link='/promotions' className='flex' />
      <div className='flex flex-row items-baseline pl3'>
        {props.promotion && <div className='font-blue-grey h2 bold'>{props.promotion.title}</div>}
      </div>
      <div className='react-table'>
        <Table columns={columns} data={props.riders} fetchData={fetchData} loading={loading} pageCount={pageCount} />
      </div>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/promotions/RidersPerPromotion.tsx